import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Field, SimpleSelect } from "@/components/wizard/Field";
import { FileUpload } from "@/components/FileUpload";
import { money } from "./common";
import { t } from "@/i18n";

export const StatementDialog = ({ open, onClose, onCreated, profiles }) => {
  const [f, setF] = useState({ user_id: "", period: "", gross: "", currency: "USD", withholding_pct: "", file_id: null, notes: "" });
  const [fname, setFname] = useState(null);
  const [busy, setBusy] = useState(false);
  const sel = profiles.find((p) => p.user.id === f.user_id);
  const rate = sel?.profile?.treaty_rate ?? 30;
  useEffect(() => { if (open) { setF({ user_id: "", period: "", gross: "", currency: "USD", withholding_pct: "", file_id: null, notes: "" }); setFname(null); } }, [open]);
  useEffect(() => { setF((x) => ({ ...x, withholding_pct: String(rate) })); }, [rate]);
  const gross = parseFloat(f.gross) || 0;
  const pct = parseFloat(f.withholding_pct) || 0;
  const net = gross - gross * pct / 100;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/statements", { ...f, gross, withholding_pct: pct });
      toast.success(t("Statement creado")); onCreated(data); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="statement-dialog">
        <DialogHeader><DialogTitle>{t("Nuevo statement")}</DialogTitle><DialogDescription>{t("Sube el reporte del periodo y registra el monto a pagar al artista.")}</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <Field label="Artista / cuenta">
            <SimpleSelect value={f.user_id} onChange={(user_id) => setF((x) => ({ ...x, user_id }))} options={profiles.map((p) => [p.user.id, `${p.user.artist_name || p.user.name} (${p.user.email})`])} testId="statement-user" />
            {sel && !sel.profile && <p className="text-xs text-amber-300">{t("Este usuario aún no registró sus datos de pago; se usa la retención estándar del 30%.")}</p>}
            {sel?.profile && sel.profile.tax_form_status !== "approved" && <p className="text-xs text-amber-300">{t("Formulario fiscal {form} no aprobado todavía.", { form: sel.profile.tax_form_required })}</p>}
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Periodo"><Input placeholder="2026-Q1" value={f.period} onChange={(e) => setF((x) => ({ ...x, period: e.target.value }))} data-testid="statement-period" /></Field>
            <Field label="Moneda"><SimpleSelect value={f.currency} onChange={(currency) => setF((x) => ({ ...x, currency }))} options={["USD", "MXN", "EUR"]} testId="statement-currency" /></Field>
            <Field label="Monto bruto"><Input type="number" step="0.01" min="0" value={f.gross} onChange={(e) => setF((x) => ({ ...x, gross: e.target.value }))} data-testid="statement-gross" /></Field>
            <Field label="Retención %" hint={sel?.profile ? t("Tasa del tratado para {c}", { c: sel.profile.country_name?.es }) : undefined}><Input type="number" step="0.5" min="0" max="100" value={f.withholding_pct} onChange={(e) => setF((x) => ({ ...x, withholding_pct: e.target.value }))} data-testid="statement-withholding" /></Field>
          </div>
          <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm"><span className="text-muted-foreground">{t("Neto a pagar")}</span><span className="font-semibold tabular-nums" data-testid="statement-net-preview">{money(net, f.currency)}</span></div>
          <FileUpload kind="statement" accept=".pdf,.csv,.xlsx,.xls" label={t("Reporte del periodo (PDF / CSV / Excel)")} value={f.file_id} filename={fname} onUploaded={(rec) => { setFname(rec.original_filename); setF((x) => ({ ...x, file_id: rec.id })); }} onClear={() => { setFname(null); setF((x) => ({ ...x, file_id: null })); }} testId="statement-file" compact />
          <Field label="Notas"><Input value={f.notes} onChange={(e) => setF((x) => ({ ...x, notes: e.target.value }))} data-testid="statement-notes" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
          <Button onClick={submit} disabled={busy || !f.user_id || !f.period || !f.gross} data-testid="statement-submit">{t("Crear statement")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
